import { useState, useEffect } from "react";
import { SCORECARD_SECTIONS } from "../data/questions";

const API =
  window.__RUNTIME_CONFIG__?.apiBase ||
  process.env.REACT_APP_API_BASE ||
  "http://127.0.0.1:5000";

// All question keys from all sections (flattened)
function allQuestions() {
  return SCORECARD_SECTIONS.flatMap((sec) =>
    sec.groups.flatMap((g) => g.questions.map((q) => ({ sec, q })))
  );
}

// Score → colour
function scoreColor(val, max = 10) {
  if (val == null) return "var(--text3)";
  const pct = val / max;
  if (pct >= 0.7) return "var(--green)";
  if (pct >= 0.4) return "var(--amber)";
  return "var(--red)";
}
function scoreBg(val, max = 10) {
  if (val == null) return "var(--bg4)";
  const pct = val / max;
  if (pct >= 0.7) return "var(--green-bg)";
  if (pct >= 0.4) return "var(--amber-bg)";
  return "var(--red-bg)";
}

// Clickable 0–10 button row
function ScorePicker({ value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {Array.from({ length: 11 }, (_, i) => i).map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => !disabled && onChange(n)}
          disabled={disabled}
          style={{
            width: 34,
            height: 34,
            borderRadius: "var(--radius-sm)",
            border: value === n
              ? `2px solid ${scoreColor(n)}`
              : "1px solid var(--border2)",
            background: value === n ? scoreBg(n) : "var(--bg4)",
            color: value === n ? scoreColor(n) : "var(--text3)",
            fontWeight: value === n ? 700 : 400,
            fontSize: 13,
            cursor: disabled ? "default" : "pointer",
            transition: "all 0.15s",
            fontFamily: "var(--font)",
          }}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

export default function ScorecardForm({ applicantId, authToken, isAdmin, testScore }) {
  const [scores, setScores]         = useState({});   // "secKey::qKey" → 0-10
  const [notes, setNotes]           = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState({ text: "", ok: true });

  // -- Load existing scorecard --
  useEffect(() => {
    if (!applicantId) return;
    setLoading(true);
    fetch(`${API}/api/admin/applicants/${applicantId}/scorecard`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.scorecard) {
          setNotes(d.scorecard.overall_notes || "");
          setIsComplete(d.scorecard.is_complete || false);
          const s = {};
          (d.scorecard.scores || []).forEach((x) => {
            s[`${x.section}::${x.question_key}`] = x.score;
          });
          // Auto-populate test score from marking tool if not already saved
          if (s["test::score"] == null && testScore != null) {
            s["test::score"] = testScore;
          }
          setScores(s);
        } else if (testScore != null) {
          // No scorecard yet — pre-fill test score
          setScores({ "test::score": testScore });
        }
        setLoading(false);
      })
      .catch(() => {
        setMsg({ text: "Could not load scorecard.", ok: false });
        setLoading(false);
      });
  }, [applicantId, authToken, testScore]);

  // -- Helpers --
  const sectionTotal = (sec) => {
    const vals = sec.groups
      .flatMap((g) => g.questions)
      .map((q) => scores[`${sec.key}::${q.key}`])
      .filter((v) => v != null);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0);
  };

  const grandTotal = () => {
    return SCORECARD_SECTIONS.reduce((acc, sec) => {
      const t = sectionTotal(sec);
      return acc + (t ?? 0);
    }, 0);
  };

  const grandMax = SCORECARD_SECTIONS.reduce((a, s) => a + s.max, 0);

  const hasAnyScore = SCORECARD_SECTIONS.some((sec) => sectionTotal(sec) != null);

  const handleReset = () => setScores({});

  const handleSave = async () => {
    try {
      setSaving(true);
      setMsg({ text: "", ok: true });
      const scoresPayload = SCORECARD_SECTIONS.flatMap((sec) =>
        sec.groups.flatMap((g) =>
          g.questions.map((q) => ({
            section: sec.key,
            question_key: q.key,
            score: scores[`${sec.key}::${q.key}`] ?? null,
          }))
        )
      );
      const res = await fetch(
        `${API}/api/admin/applicants/${applicantId}/scorecard`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            overall_notes: notes,
            is_complete: isComplete,
            scores: scoresPayload,
          }),
        }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not save");
      setMsg({ text: "Scorecard saved.", ok: true });
    } catch (e) {
      setMsg({ text: e.message, ok: false });
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <p style={{ color: "var(--text3)", fontSize: 13 }}>Loading scorecard…</p>;

  const total = grandTotal();

  return (
    <div>
      {/* ── Admin notice ── */}
      <div
        style={{
          marginBottom: 20,
          padding: "8px 14px",
          background: "var(--amber-bg)",
          border: "1px solid rgba(232,169,74,0.25)",
          borderRadius: "var(--radius-sm)",
          fontSize: 12,
          color: "var(--amber)",
        }}
      >
        This scorecard is internal — the applicant cannot see it.
      </div>

      {/* ── Overall summary bar ── */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 24,
          alignItems: "stretch",
        }}
      >
        {/* Grand total */}
        <div
          style={{
            padding: "14px 20px",
            background: hasAnyScore ? scoreBg(total, grandMax) : "var(--bg3)",
            border: `1px solid ${hasAnyScore ? scoreColor(total, grandMax) + "44" : "var(--border)"}`,
            borderRadius: "var(--radius)",
            minWidth: 110,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: "var(--text2)",
              marginBottom: 4,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Total
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: hasAnyScore ? scoreColor(total, grandMax) : "var(--text3)",
              fontFamily: "var(--font-display)",
            }}
          >
            {hasAnyScore ? total : "—"}
            {hasAnyScore && (
              <span style={{ fontSize: 13, fontWeight: 400, color: "var(--text2)" }}>
                /{grandMax}
              </span>
            )}
          </div>
          {isComplete && (
            <div style={{ marginTop: 4, fontSize: 11, color: "var(--green)" }}>
              ✓ Complete
            </div>
          )}
        </div>

        {/* Per-section badges */}
        {SCORECARD_SECTIONS.map((sec) => {
          const t = sectionTotal(sec);
          return (
            <div
              key={sec.key}
              style={{
                padding: "14px 16px",
                background: t != null ? scoreBg(t, sec.max) : "var(--bg3)",
                border: `1px solid ${t != null ? scoreColor(t, sec.max) + "44" : "var(--border)"}`,
                borderRadius: "var(--radius)",
                textAlign: "center",
                minWidth: 90,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "var(--text2)",
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                }}
              >
                {sec.label}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: t != null ? scoreColor(t, sec.max) : "var(--text3)",
                  fontFamily: "var(--font-display)",
                }}
              >
                {t != null ? t : "—"}
                {t != null && (
                  <span style={{ fontSize: 12, fontWeight: 400, color: "var(--text2)" }}>
                    /{sec.max}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Scoring guide ── */}
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          gap: 16,
          fontSize: 12,
          color: "var(--text2)",
        }}
      >
        <span style={{ color: "var(--red)" }}>■</span> 0–3 Poor &nbsp;
        <span style={{ color: "var(--amber)" }}>■</span> 4–6 Satisfactory &nbsp;
        <span style={{ color: "var(--green)" }}>■</span> 7–10 Strong &nbsp;
        {isAdmin && (
          <button
            type="button"
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: "var(--text3)",
              background: "none",
              border: "1px solid var(--border2)",
              borderRadius: "var(--radius-sm)",
              padding: "2px 10px",
              cursor: "pointer",
            }}
            onClick={handleReset}
          >
            Reset all scores
          </button>
        )}
      </div>

      {/* ── Sections ── */}
      {SCORECARD_SECTIONS.map((sec) => {
        const secTotal = sectionTotal(sec);
        const allQuestions = sec.groups.flatMap((g) => g.questions);
        let questionNumber = 0; // running counter across groups

        return (
          <div
            key={sec.key}
            style={{
              marginBottom: 16,
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              overflow: "hidden",
            }}
          >
            {/* Section header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 18px",
                background: "var(--bg3)",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 14 }}>{sec.label}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, color: "var(--text2)" }}>
                  Max: {sec.max}
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: secTotal != null ? scoreColor(secTotal, sec.max) : "var(--text3)",
                  }}
                >
                  {secTotal != null ? `${secTotal}/${sec.max}` : `—/${sec.max}`}
                </span>
              </div>
            </div>

            {/* Groups */}
            <div style={{ padding: "16px 18px" }}>
              {sec.groups.map((group, gi) => (
                <div key={gi}>
                  {/* Sub-group header */}
                  {group.label && (
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "var(--text2)",
                        marginBottom: 12,
                        marginTop: gi > 0 ? 20 : 0,
                        paddingBottom: 6,
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      {group.label}
                    </div>
                  )}

                  {/* Questions */}
                  {group.questions.map((q, qi) => {
                    questionNumber++;
                    const k = `${sec.key}::${q.key}`;
                    const val = scores[k] ?? null;
                    const isLastInGroup = qi === group.questions.length - 1;
                    const isLastGroup = gi === sec.groups.length - 1;

                    // For singleScore sections show a plain number input
                    if (sec.singleScore) {
                      return (
                        <div key={q.key}>
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--text2)",
                              marginBottom: 10,
                            }}
                          >
                            {q.label}
                          </div>
                          {isAdmin ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <input
                                type="number"
                                min={0}
                                max={sec.max}
                                value={val ?? ""}
                                onChange={(e) => {
                                  const v = e.target.value === "" ? null : Math.min(sec.max, Math.max(0, Number(e.target.value)));
                                  setScores((p) => ({ ...p, [k]: v }));
                                }}
                                placeholder={`0 – ${sec.max}`}
                                style={{
                                  width: 80,
                                  padding: "6px 10px",
                                  fontSize: 16,
                                  fontWeight: 700,
                                  textAlign: "center",
                                  background: "var(--bg4)",
                                  border: "1px solid var(--border2)",
                                  borderRadius: "var(--radius-sm)",
                                  color: val != null ? scoreColor(val, sec.max) : "var(--text)",
                                }}
                              />
                              <span style={{ fontSize: 13, color: "var(--text3)" }}>
                                {val != null ? "auto-filled from test marking tool" : `out of ${sec.max} — enter from the test marking tool`}
                              </span>
                            </div>
                          ) : (
                            <div style={{ fontSize: 18, fontWeight: 700, color: val != null ? scoreColor(val, sec.max) : "var(--text3)" }}>
                              {val != null ? `${val}/${sec.max}` : "Not entered"}
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={q.key}
                        style={{
                          marginBottom: (!isLastInGroup || !isLastGroup) ? 18 : 0,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: 8,
                            gap: 12,
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>
                            <span style={{ color: "var(--text3)", marginRight: 6, fontSize: 12 }}>
                              {questionNumber}.
                            </span>
                            {q.label}
                          </span>
                          {val != null && (
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: scoreColor(val),
                                minWidth: 32,
                                textAlign: "right",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {val}/10
                            </span>
                          )}
                        </div>
                        {q.yesNo ? (
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {[{ label: "Yes", score: q.yesScore }, { label: "No", score: q.noScore }].map(({ label, score }) => {
                              const selected = val === score;
                              const colour = selected ? (score >= 7 ? "var(--green)" : score >= 4 ? "var(--amber)" : "var(--red)") : "var(--text3)";
                              const bg = selected ? (score >= 7 ? "var(--green-bg)" : score >= 4 ? "var(--amber-bg)" : "var(--red-bg)") : "var(--bg4)";
                              return (
                                <button key={label} type="button" disabled={!isAdmin}
                                  onClick={() => isAdmin && setScores((p) => ({ ...p, [k]: score }))}
                                  style={{ padding: "6px 24px", borderRadius: "var(--radius-sm)", border: selected ? `2px solid ${colour}` : "1px solid var(--border2)", background: bg, color: colour, fontWeight: selected ? 700 : 400, fontSize: 14, cursor: isAdmin ? "pointer" : "default", fontFamily: "var(--font)" }}
                                >{label}</button>
                              );
                            })}
                            {val != null && <span style={{ fontSize: 12, color: "var(--text3)" }}>→ {val}/10</span>}
                          </div>
                        ) : (
                          <ScorePicker
                            value={val}
                            onChange={(n) => setScores((p) => ({ ...p, [k]: n }))}
                            disabled={!isAdmin}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* ── Summary table ── */}
      {hasAnyScore && (
        <div
          style={{
            marginTop: 8,
            marginBottom: 24,
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "10px 18px",
              background: "var(--bg3)",
              borderBottom: "1px solid var(--border)",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Score Summary
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {SCORECARD_SECTIONS.map((sec) => {
                const t = sectionTotal(sec);
                return (
                  <tr
                    key={sec.key}
                    style={{ borderBottom: "1px solid var(--border)" }}
                  >
                    <td style={{ padding: "10px 18px", color: "var(--text2)" }}>
                      {sec.label}
                    </td>
                    <td style={{ padding: "10px 18px", textAlign: "right" }}>
                      <span
                        style={{
                          fontWeight: 700,
                          color: t != null ? scoreColor(t, sec.max) : "var(--text3)",
                        }}
                      >
                        {t != null ? t : "—"}
                      </span>
                      <span style={{ color: "var(--text3)", fontSize: 12 }}>
                        /{sec.max}
                      </span>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: "var(--bg3)" }}>
                <td style={{ padding: "12px 18px", fontWeight: 700 }}>Total</td>
                <td style={{ padding: "12px 18px", textAlign: "right" }}>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 16,
                      color: scoreColor(total, grandMax),
                    }}
                  >
                    {total}
                  </span>
                  <span style={{ color: "var(--text3)", fontSize: 12 }}>
                    /{grandMax}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ── Notes + save (admin only) ── */}
      {isAdmin && (
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 16 }}>
            <label className="label">Overall Notes / Recommendation</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Summary, placement recommendation, concerns…"
            />
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              marginBottom: 16,
              fontSize: 14,
            }}
          >
            <input
              type="checkbox"
              checked={isComplete}
              onChange={(e) => setIsComplete(e.target.checked)}
              style={{ width: "auto" }}
            />
            <span style={{ fontWeight: 500 }}>Mark scorecard as complete</span>
          </label>

          {msg.text && (
            <p
              style={{
                marginBottom: 12,
                fontSize: 13,
                color: msg.ok ? "var(--green)" : "var(--red)",
              }}
            >
              {msg.text}
            </p>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Scorecard"}
          </button>
        </div>
      )}
    </div>
  );
}
